<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <breadcrumbs
      class="main-product__breadcrumbs"
      :title="title"
      type="product"
      state="loaded"
      :breadcrumbs="liquid.breadcrumbs"
    />

    <div class="main-product__grid grid">
      <div
        id="product-gallery"
        class="col xs-span l7"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="tabs-vertical"
          :loading="state === 'loading'"
          :product="product || liquid"
          :variant="variant"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div class="main-product__sticky col xs-span l5 xl9-11">
        <div class="main-product__details">
          <product-vendor
            class="main-product__vendor text-body-s-regular-desktop"
            :product="product || liquid"
          />

          <h1
            id="template-title"
            class="
              main-product__title
              text-heading-l
              text-heading-2xl-desktop
            "
            v-html="title"
          />

          <div
            v-if="
              $variable('settings.enableAppioRatings') ||
                $variable('settings.reviewsIo.enable')
            "
            ref="ratingWidget"
            role="button"
            tabindex="0"
            class="main-product__rating"
            :class="{ [$class('hidden')]: hideRatingWidget }"
            @click="scrollToReviews"
            @keydown.enter="scrollToReviews"
          />

          <div class="main-product__prices-row">
            <product-prices
              class="main-product__prices-container"
              :available="selectedVariant ? selectedVariant?.available : true"
              :compare-at-price="
                selectedVariant
                  ? selectedVariant.compareAtPrice?.amount
                  : liquid.compareAtPrice?.amount
              "
              :loading="state === 'loading'"
              :price="
                selectedVariant
                  ? selectedVariant?.price?.amount
                  : liquid.price?.amount
              "
              :varies="productPriceVaries"
            />

            <div
              v-if="selectedVariant && getBadgeInfo(selectedVariant)?.text"
              class="main-product__badge-container"
            >
              <content-badge
                :badge-text="getBadgeInfo(selectedVariant).text"
                :color-scheme="getBadgeInfo(selectedVariant).colorScheme"
                template="sticker"
              >
                <template #icon>
                  <component
                    :is="getBadgeInfo(selectedVariant).icon"
                    aria-hidden="true"
                  />
                </template>
              </content-badge>
            </div>
          </div>
        </div>

        <product-compatibility
          v-if="!product.giftCard && !liquid.metafields.hideCompatibility"
          class="main-product__compatibility"
          accordion
          :product="product || liquid"
        />

        <product-form
          class="main-product__form"
          :class="{ 'main-product__form--gift': product.giftCard }"
          :autofix-categories="autofixCategories"
          :loading="state === 'loading'"
          :product="product || liquid"
          :section="section"
          :variant="selectedVariant"
          @update-variant="handleVariantUpdate"
        />

        <product-usps
          class="main-product__usps"
          :free-shipping-message-enable="freeShippingMessageEnable"
          :product="product || liquid"
          :variant="variant"
        />

        <product-upsell
          class="main-product__upsell"
          :product="product || liquid"
        />
      </div>

      <section
        v-if="description"
        class="main-product__description col xs-span l7"
      >
        <h2
          class="text-heading-m text-heading-l-desktop"
          v-text="$string('product.description.title')"
        />

        <rte-content
          v-show="isDescriptionTruncated && !showEntireDescription"
          :content="descriptionShort"
          font-size="s"
          font-size-desktop="l"
          aria-hidden="true"
        />

        <rte-content
          v-show="!isDescriptionTruncated || showEntireDescription"
          :content="description"
          font-size="s"
          font-size-desktop="l"
        />

        <btn
          v-if="isDescriptionTruncated && !showEntireDescription"
          class-label="text-body-s-underline text-body-m-underline-desktop"
          :label="$string('product.description.read_more_html', { replace: { title }})"
          modifiers="text"
          type="button"
          @click="showEntireDescription = true"
        />
      </section>

      <product-details
        class="col xs-span l7"
        :data="
          variant?.metafields?.moreInformation ||
            product.metafields?.moreInformation ||
            liquid.metafields.moreInformation
        "
        is-tile-button
        :namespace="`info-${product.id}`"
        num-columns="1"
        :product="product"
        :title="$string('product.section_titles.more_information')"
      />

      <product-details
        class="col xs-span l7"
        :data="
          variant?.metafields?.benefits ||
            product.metafields?.benefits ||
            liquid.metafields.benefits
        "
        is-padding-large
        :namespace="`benefits-${product.id}`"
        :title="$string('product.section_titles.benefits')"
      />

      <product-details
        class="col xs-span l7"
        :circles="section.productIssuesCircles"
        :contents-data="contentsData"
        :data="
          variant?.metafields?.commonIssues ||
            product.metafields?.commonIssues ||
            liquid.metafields.commonIssues
        "
        :namespace="`issues-${product.id}`"
        :subheading="$string('product.section_titles.common_issues_subheading')"
        :title="
          variant?.metafields?.commonIssues?.length > 1 ||
            product.metafields?.commonIssues?.length > 1||
            liquid.metafields.commonIssues?.length > 1
            ? $string('product.section_titles.common_issues.plural')
            : $string('product.section_titles.common_issues.single')
        "
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} [autofixCategories] - Vehicle type to autofix category
 * mapping from theme settings.
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs'
import Btn from '~global/btn/btn'
import ContentBadge from '~async/content-badge/content-badge'
import ProductCompatibility from '~async/product-compatibility/product-compatibility'
import ProductDetails from '~async/product-details/product-details'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import ProductUpsell from '~async/product-upsell/product-upsell'
import ProductUsps from '~async/product-usps/product-usps'
import ProductVendor from '~async/product-vendor/product-vendor'
import RteContent from '~async/rte-content/rte-content'

import IconBellFill from '~icons/general/bell-fill.svg'

import productQuery from '~graphql/product.gql'
import { formatProduct } from '~/format/format-product'

import { formatProductForBloomreachreach, trackBloomreachEvent } from '~/helpers/bloomreach'
import { buildTaxonomyMetafieldIdentifiers } from '~/helpers/garage'
import { emitUpdateRequestObject } from '~/helpers/vue'

export default {
  name: 'MainProduct',

  components: {
    Breadcrumbs,
    Btn,
    ContentBadge,
    ProductCompatibility,
    ProductDetails,
    ProductForm,
    ProductGallery,
    ProductPrices,
    ProductUpsell,
    ProductUsps,
    ProductVendor,
    RteContent,
  },

  props: {
    autofixCategories: {
      type: Object,
      default: () => ({}),
    },
    freeShippingMessageEnable: {
      type: Boolean,
      default: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
        breadcrumbs: [],
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: true,
        id: '',
        initialSelect: true,
        productDescriptionChars: 300,
        productIssuesCircles: [],
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
      }),
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: this.$variable('page.handle'),
      hideRatingWidget: true,
      showEntireDescription: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Compute contents drawer data.
     * @returns {Boolean|Object}
     */
    contentsData() {
      return this.variant?.metafields?.contents ||
        this.product?.metafields?.contents ||
        this.liquid?.metafields?.contents
    },

    /**
     * Get the unformatted description.
     * @returns {String}
     */
    description() {
      return (
        this.variant?.metafields?.descriptionHtml ||
        this.variant?.metafields?.description ||
        this.product.metafields?.descriptionHtml ||
        this.product.metafields?.description ||
        this.product.description ||
        this.liquid.metafields.description ||
        this.liquid.description
      )
    },

    /**
     * Compute formatted short description.
     * @returns {String}
     */
    descriptionShort() {
      let description = this.description

      if (!description) {
        return ''
      }

      const maxLength = this.section.productDescriptionChars
      let truncatedIndex = description.lastIndexOf(' ', maxLength)

      if (truncatedIndex === -1) {
        truncatedIndex = maxLength
      }

      description = description.substring(0, truncatedIndex).trimEnd()
      return `${description}…`
    },

    /**
     * Compute if the product description is longer than the max length.
     * @returns {Boolean}
     */
    isDescriptionTruncated() {
      return this.description?.length > this.section.productDescriptionChars
    },

    /**
     * Compute price varies.
     * @return {Boolean}
     */
    productPriceVaries() {
      return this.selectedVariant
        ? this.selectedVariant?.price?.varies
        : this.liquid.price?.varies
    },

    /**
     * Compute Reviews.io SKU data attribute.
     * @returns {String}
     */
    reviewsIoSku() {
      if (
        !this.$variable('settings.reviewsIo.enable') ||
        !this.product ||
        !this.product?.variants?.length
      ) {
        return ''
      }

      const productType = this.product?.type.toLowerCase()
      if (productType === 'autofix') {
        return ''
      }

      const skus = this.product?.variants?.map((variant) => variant.sku)
      const ids = this.product?.variants?.map((variant) => variant.id)

      if (!skus.length || !ids.length) {
        return ''
      }

      return `${this.product.handle};${skus?.join(';')};${ids?.join(';')}`
    },

    /**
     * Compute 'selected' variant based on initial select section setting.
     * @param {Boolean|Object}
     */
    selectedVariant() {
      let variant = this.variant

      if (variant) {
        return variant
      }

      if (this.section.initialSelect) {
        variant = this.liquid.selectedOrFirstAvailableVariant
        this.handleVariantUpdate(variant)
      }

      return variant
    },

    /**
     * Compute product taxonomy metafield identifiers.
     * @returns {Array}
     */
    taxonomyMetafieldIdentifiers() {
      return buildTaxonomyMetafieldIdentifiers()
    },

    /**
     * Compute title.
     * @returns {String}
     */
    title() {
      return this.variant && this.variant.metafields.title
        ? this.variant.metafields.title
        : this.product.title || this.liquid.title
    },

    /**
     * Compute if product is Autofix based on product type.
     * @returns {Boolean}
     */
    isAutofixProduct() {
      const productType = this.product?.type.toLowerCase()
      return productType === 'autofix'
    },
  },

  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle)
    },

    /**
     * Watch title property to update SEO data.
     */
    title() {
      document.title = `${this.liquid.title} – ${cnvs.store.shopName}`
    },

    /**
     * Handle product change functionality.
     */
    product: {
      handler() {
        this.moveRatingWidget()
        this.queryProductRating()
      },
      immediate: true,
    },
  },

  mounted() {
    this.setProduct()
    this.sendBloomreachProductViewEvent()
  },

  methods: {

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
            taxonomyMetafieldIdentifiers: this.taxonomyMetafieldIdentifiers,
          },
        })

        if (this.$variable('settings.disableComponentReadyState')) {
          return
        }

        const formattedProduct = formatProduct(response)

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        this.updateProductUrl(formattedProduct.handle)
        this.state = 'ready'

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0]

      if (oldHandle === handle) {
        return
      }

      const pathname = location.pathname.replace(oldHandle, handle)
      const url = `${location.origin}${pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })
    },

    /**
     * Compute badge data.
     * @param {String} vaue - Option value.
     * @returns {Boolean|Object}
     */
    getBadgeInfo(variant) {
      const badge = {
        colorScheme: 'yellow',
        icon: null,
        text: '',
      }

      if (variant.metafields.badgeText) {
        badge.text = variant.metafields.badgeText
      }

      if (!variant.available) {
        badge.colorScheme = 'dark-grey'
        badge.icon = IconBellFill
        badge.text = this.$string('product.prices.sold_out')
      }

      return badge
    },

    /**
     * Helper function to call Bloomreach PDP view event.
     */
    sendBloomreachProductViewEvent() {
      const product = this.product || this.liquid

      trackBloomreachEvent(
        'view_item',
        formatProductForBloomreachreach(product, this.variant),
      )
    },

    /**
     * Moves the Appio and/or Reviews.io rating widget into the Vue ref
     * container after DOM updates.
    */
    async moveRatingWidget() {
      if (this.isAutofixProduct) {
        return
      }

      await this.$nextTick()
      await cnvs.Sleep(1000)

      const target = this.$refs.ratingWidget
      let hideRatingWidget = true

      if (!target) {
        return
      }

      if (this.$variable('settings.reviewsIo.enable')) {
        const reviewsIoWidget = document.querySelector('#vue-element .ruk_rating_snippet')

        if (reviewsIoWidget && !target.contains(reviewsIoWidget)) {
          target.appendChild(reviewsIoWidget)
        }

        if (target.contains(reviewsIoWidget)) {
          hideRatingWidget = false
        }
      }

      if (this.$variable('settings.enableAppioRatings')) {
        const appioWidget = document.querySelector('.appio-rating-badge.appio-rating-major')

        if (appioWidget && !target.contains(appioWidget)) {
          target.appendChild(appioWidget)
        }

        if (target.contains(appioWidget)) {
          hideRatingWidget = false
        }
      }

      this.hideRatingWidget = hideRatingWidget
    },

    /**
     * Query product rating from Reviews.io and update product schema.
     */
    async queryProductRating() {
      if (
        !this.product ||
        !this.product?.variants?.length ||
        !this.$variable('settings.reviewsIo.enable') ||
        !this.$variable('settings.reviewsIo.apiUrl') ||
        !this.$variable('settings.reviewsIo.store') ||
        this.isAutofixProduct
      ) {
        return
      }

      try {
        const url = this.$variable('settings.reviewsIo.apiUrl')
          .replace('{{ store }}', this.$variable('settings.reviewsIo.store'))
          .replace('{{ sku }}', this.reviewsIoSku)

        let response = await fetch(url)

        if (!response.ok) {
          throw Error
        }

        response = await response.json()

        /**
         * Build review count and rating value.
         */
        let reviewCount = 0
        let ratingValue = 0

        response.forEach((sku) => {
          ratingValue += Number(sku.average_rating) * sku.num_ratings
          reviewCount += sku.num_ratings
        })

        if (!reviewCount) {
          return
        }

        ratingValue /= reviewCount
        ratingValue = Number(ratingValue.toFixed(2))

        this.updateProductSchema(ratingValue, reviewCount)

      } catch (error) {
        cnvs.ReportError('Failed to load Reviews.io API', error)
      }
    },

    /**
     * Update product schema JSON+LD.
     * @param {Number} ratingValue - Average rating rounded to two decimals.
     * @param {Number} reviewCount - Total number of reviews.
     */
    updateProductSchema(ratingValue, reviewCount) {
      try {
        const scriptElement = document.querySelector('[js-product="schema"]')

        if (!scriptElement) {
          return
        }

        const schema = JSON.parse(scriptElement.innerHTML)
        let productSchema = schema[0]

        productSchema = {
          ...productSchema,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingCount: reviewCount,
            ratingValue,
            reviewCount,
          },
        }

        schema.splice(0, 1, productSchema)
        scriptElement.innerHTML = JSON.stringify(schema, null, 2).replaceAll('/', '\\/')

      } catch (error) {
        cnvs.ReportError('Failed to update product reviews schema', error)
      }
    },

    /**
      * Scroll to the ReviewsWidget section when rating widget is clicked.
      */
    scrollToReviews() {
      let target = null

      if (this.$variable('settings.reviewsIo.enable')) {
        target = document.querySelector('#ReviewsWidget')
      } else if (this.$variable('settings.enableAppioRatings')) {
        target = document.querySelector('.appio-reviews-widget')
      }

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'

      if (target) {
        target.scrollIntoView({
          behavior,
          block: 'start',
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
